import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  BulkCreateResponse,
  CaseEndpoint,
  EndpointRelationship,
} from '../../types/case-container'
import {
  EndpointList,
  ErrorBanner,
  Footer,
  ModalHeader,
  SearchBar,
  SuccessView,
  TargetIndicator,
} from './copy-links-from-modal/copy-links-from-modal.parts'
import { useCopyLinksFromModal } from './copy-links-from-modal/use-copy-links-from-modal'

interface CopyLinksFromModalProps {
  targetEndpoint: CaseEndpoint
  endpoints: CaseEndpoint[]
  relationships: EndpointRelationship[]
  onClose: () => void
  onCopy: (fromEndpointId: string) => Promise<BulkCreateResponse | null>
  copying: boolean
  error: string | null
}

export function CopyLinksFromModal(props: CopyLinksFromModalProps): ReactElement {
  const { targetEndpoint, endpoints, relationships, onClose, onCopy, copying, error } = props
  const { t } = useTranslation('cases')
  const state = useCopyLinksFromModal({ targetEndpoint, endpoints, relationships, onCopy, onClose })

  if (state.copySuccess) {
    return (
      <SuccessView
        count={state.copySuccess.count}
        hostname={targetEndpoint.hostname}
        onClose={state.handleClose}
        t={t}
      />
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl dark:bg-slate-900">
        <ModalHeader onClose={state.handleClose} t={t} />
        <TargetIndicator hostname={targetEndpoint.hostname} t={t} />
        <SearchBar searchTerm={state.searchTerm} setSearchTerm={state.setSearchTerm} t={t} />
        {error && <ErrorBanner error={error} />}
        <EndpointList
          filteredEndpoints={state.filteredEndpoints}
          selectedEndpointId={state.selectedEndpointId}
          endpointLinkCounts={state.endpointLinkCounts}
          setSelectedEndpointId={state.setSelectedEndpointId}
          searchTerm={state.searchTerm}
          t={t}
        />
        <Footer
          filteredCount={state.filteredEndpoints.length}
          selectedLinkCount={state.selectedLinkCount}
          selectedEndpointId={state.selectedEndpointId}
          copying={copying}
          onClose={state.handleClose}
          onCopy={() => {
            void state.handleCopy()
          }}
          t={t}
        />
      </div>
    </div>
  )
}
